import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import './receipt-donation.scss';
import { DONATION_TYPES, getLanguageDefault, Regex_Emoji, URL_PATHNAME } from '../../../../../utils/Utils';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Column, ColumnAlignType } from 'primereact/column';
import {
  checkDateInRange3Years,
  checkDateInRange56Days,
  checkIsRO3,
  generateFileDownload,
  parseFloatByLanguage,
  showNotification,
} from '../../../../../utils/functionCommon';
import moment from 'moment';
import { Button } from 'primereact/button';
import { Formik } from 'formik';
import * as Yup from 'yup';
import DonorManagerAPI from '../../../../../services/donorAPI';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { withTranslation } from 'react-i18next';
import { getCountryOptions } from '../../../../../utils/countryHelper';

const ReceiptDonation = (props: any) => {
  const { donation, formatDate, token, id, toast, getDonationDetails, donationReceipts, isLoading, isSubscriptionDonation, t, i18n } = props;

  const formikRef = useRef(null);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const navigate = useNavigate();
  const [isEdittingReceipt, setIsEdittingReceipt] = useState(false);
  const [formState, setFormState] = useState({ isValid: false, dirty: false });
  const language: any = getLanguageDefault().code.toLowerCase();

  const countries = useMemo(() => {
    return getCountryOptions(language || 'de');
  }, [getLanguageDefault()]);

  const downloadReceipt = async (donation: any, year: any, isParent: any) => {
    await onHandleDownloadReceipt(donation, year, isParent);
  };

  const isEmptyPayment = () => {
    return (
      !donation?.payment_address?.trim() ||
      !donation?.payment_post_code?.trim() ||
      !donation?.payment_city?.trim() ||
      !donation?.payment_country?.trim()
    );
  };

  const onHandleDownloadReceipt = async (donation: any, year: any, isParent: any) => {
    try {
      setIsLoadingAction(true);
      const languageCode = donation?.payment_currency_code === 'eur' ? 'de' : 'en';
      const pdfResponse =
        isSubscriptionDonation(donation) && !isParent
          ? await DonorManagerAPI.getRecurringReceiptByYear(donation.id, year, {}, languageCode, token!)
          : await DonorManagerAPI.getReceipt(donation.id, {}, languageCode, token!);
      if (pdfResponse && pdfResponse.status === 200) {
        const prefixReceiptName = languageCode === 'de' ? 'Spendenquittung' : 'Receipt';
        const receiptName =
          isSubscriptionDonation(donation) && !isParent ? `${prefixReceiptName}_${donation.serial}_${+year + 1}` : `${prefixReceiptName}_${donation.serial}`;
        generateFileDownload(receiptName, pdfResponse.data.content);
        setIsLoadingAction(false);
        showNotification('success', t('download_receipt_success'), toast);
        return;
      }
    } catch (error) {
      console.log('onHandleDownloadReceipt: ' + error);
    }

    setIsLoadingAction(false);
    showNotification('error', t('download_receipt_error'), toast);
  };

  const updateDonorDonationAsync = async (donation_id: string, data: any) => {
    try {
      const { data: updateResponse } = await DonorManagerAPI.updateDonorDonation(donation_id, data, token || '');

      if (updateResponse === 'Session is expired!') {
        navigate(URL_PATHNAME.DONOR_PORTAL);
      }

      return updateResponse?.isValid;
    } catch (error) {
      console.log('updateDonorDonationAsync: ' + error);
    }

    return false;
  };

  const onHandleSaveReceipt = async () => {
    setIsEdittingReceipt(false);
    try {
      // @ts-ignore: Object is possibly 'null'.
      const { values, dirty, isValid } = formikRef?.current;
      // @ts-ignore: Object is possibly 'null'.
      formikRef.current.validateForm();
      // @ts-ignore: Object is possibly 'null'.
      if (formikRef && dirty && isValid) {
        const updateResponse = await updateDonorDonationAsync(donation.id, {
          ...values,
        });

        if (updateResponse) {
          getDonationDetails(id);
          showNotification('success', t('save_receipt_success'), toast);
          return;
        }
      }
    } catch (error) {
      showNotification('error', t('save_receipt_error'), toast);
    }
  };

  const initialFormValues = () => {
    const initialReceipt = {
      payment_address: donation && donation.payment_address ? donation.payment_address?.trim() : '',
      payment_additional_address: donation && donation.payment_additional_address ? donation.payment_additional_address?.trim() : '',
      payment_post_code: donation && donation.payment_post_code ? donation.payment_post_code?.trim() : '',
      payment_city: donation && donation.payment_city ? donation.payment_city?.trim() : '',
      payment_country: donation && donation.payment_country !== null ? donation.payment_country?.toUpperCase()?.trim() : '',
    };
    return initialReceipt;
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        payment_address: Yup.string().nullable().required(t('field_validate')),
        payment_post_code: Yup.string().nullable().required(t('field_validate')),
        payment_city: Yup.string().nullable().required(t('field_validate')),
      }),
    [i18n.language],
  );

  useEffect(() => {
    // @ts-ignore: Object is possibly 'null'.
    formikRef.current?.validateForm();
  }, [i18n.language]);

  const selectedTemplate = (option: any, props: any) => {
    if (option) {
      return (
        <span className="p-float-label" style={{ height: '50px' }}>
          <InputText
            className="float-input"
            name="payment_land"
            value={option?.name}
            disabled={true}
            style={{ border: '0px', padding: '10px 0px 0px 0px', backgroundColor: 'transparent', background: 'transparent', opacity: '1' }}
            readOnly={true}
            id="payment_land"
          />
          <label style={{ paddingLeft: '0px', left: '0px' }} className="label" htmlFor="payment_land">
            {t('select_country')} <span>*</span>
          </label>
        </span>
      );
    }

    return <span>{props.placeholder}</span>;
  };

  const validateReceiptPdf = (rowData: any, donation: any) => {
    const dateInRange3Years = checkDateInRange3Years(rowData.payment_date);
    const dateInRange56Days = checkDateInRange56Days(rowData.payment_date, donation?.receipt_template_id);
    if (!rowData.isParent) {
      return {
        message: t('available'),
        isDisabled: false,
      };
    }

    if (donation.donation_type !== DONATION_TYPES.CODE) {
      if (dateInRange56Days) {
        const next56daysDate = moment(donation.payment_date).add(checkIsRO3(donation.receipt_template_id), 'days').format('DD.MM.YYYY');
        return {
          message: `${t('from')} ${next56daysDate}`,
          isDisabled: true,
        };
      } else if (!dateInRange3Years) {
        return {
          message: t('not_available'),
          isDisabled: true,
        };
      }
    } else {
      if (!dateInRange3Years) {
        return {
          message: t('not_available'),
          isDisabled: true,
        };
      }
    }

    return {
      message: t('available'),
      isDisabled: false,
    };
  };

  const tableColumnsReceipt = [
    {
      field: 'payment_date',
      header: (a: any) => {
        return t('receipt_table_header_0');
      },
      width: '25%',
      align: 'left',
      bodyTemPlate: (rowData: any) => {
        return formatDate(rowData.payment_date);
      },
    },
    {
      field: 'total_amount',
      header: (a: any) => {
        return t('receipt_table_header_1');
      },
      width: '25%',
      align: 'left',
      bodyTemPlate: (rowData: any) => {
        let value = rowData.total_amount;
        value = Math.max(+value - +(rowData.match_funding_value ? rowData.match_funding_value : rowData.coupon_value || 0), 0);
        return (
          <div>
            {rowData ? parseFloatByLanguage(value, t('language')) : '-'} <span> {rowData.currency_unit || '€'} </span>
          </div>
        );
      },
    },
    {
      field: '',
      header: (a: any) => {
        return t('receipt_table_header_2');
      },
      width: '25%',
      align: 'left',
      bodyTemPlate: (rowData: any) => {
        const { message, isDisabled } = validateReceiptPdf(rowData, donation);
        return <div>{message} </div>;
      },
    },
    {
      field: '',
      header: '',
      width: '25%',
      alignFrozen: 'right',
      align: 'right',
      bodyTemPlate: (rowData: any) => {
        const { message, isDisabled } = validateReceiptPdf(rowData, donation);
        return (
          <>
            <Tooltip target=".disabled-button" />
            <span
              className="disabled-button mr-2"
              data-pr-tooltip={`${isEmptyPayment() ? t('tooltip_message') : ''}`}
              data-pr-position="top"
              data-pr-at="right+5 top"
              data-pr-my="left center-2"
            >
              <Button
                icon="fa-solid fa-download"
                disabled={isEmptyPayment() || isDisabled || isEdittingReceipt || donation?.disabled_receipt}
                onClick={() => downloadReceipt(donation, rowData.year, rowData.isParent)}
              />
            </span>
          </>
        );
      },
    },
  ];

  const dynamicColumnsReceipt = tableColumnsReceipt.map((col, i) => {
    return (
      <Column
        key={col.field}
        field={col.field}
        header={col.header}
        style={{
          width: col.width,
        }}
        body={col.bodyTemPlate}
        align={col.align as ColumnAlignType}
      />
    );
  });

  const onResetForm = () => {
    // @ts-ignore: Object is possibly 'null'.
    formikRef.current?.resetForm();
    setIsEdittingReceipt(false);
  };

  return (
    <div className="receipt-donation">
      {isLoadingAction && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="loading-icon"></div>
          </div>
        </div>
      )}
      <div className="receipt-donation-container">
        <div className="donation-details-body bg-gray bg-linear">
          <div className="aw_container donation-details-body-container">
            <div className="donation-certificate">
              <div className="donation-certificate-container">
                <div className="donation-receipt-container row">
                  <div className="donation-certificate-section col-md-6 col-12">
                    <div className="donation-certificate-field">
                      <div className="donation-certificate-content-title">{t('address')}</div>
                      <Formik
                        innerRef={formikRef}
                        initialValues={initialFormValues()}
                        validationSchema={validationSchema}
                        onSubmit={(values, { setSubmitting }) => {
                          // console.log("values >>>", values);
                        }}
                      >
                        {({ values, errors, touched, dirty, isValid, handleBlur, handleSubmit, setFieldValue, handleChange, resetForm }) => {
                          if (formState.isValid !== isValid || formState.dirty !== dirty) {
                            setFormState({ isValid, dirty });
                          }
                          return (
                            <form onSubmit={handleSubmit}>
                              <div className="row-content">
                                <div className="row">
                                  <div className="col-md-12">
                                    <div className="input-item">
                                      <span className="p-float-label">
                                        <InputText
                                          name="payment_full_name"
                                          value={
                                            (donation?.payment_company_name && (donation.payment_method_code === 'card' || donation.is_imported)
                                              ? `${donation?.payment_company_name} - `
                                              : '') + (donation.receipt_name || '-')
                                          }
                                          disabled={true}
                                          id="payment_full_name"
                                        />
                                        <label className="label" htmlFor="payment_full_name">
                                          {t('name_donation_receipt')} <span>*</span>
                                        </label>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-12">
                                    <div className="input-item">
                                      <span className="p-float-label">
                                        <InputText
                                          className={`${touched.payment_address && errors.payment_address ? 'has-error' : ''}`}
                                          name="payment_address"
                                          key={isEdittingReceipt ? 'enabled' : 'disabled'}
                                          onChange={(item: any) => {
                                            const text = item.target.value;
                                            const sanitizedText = text.replace(Regex_Emoji, '');
                                            if (sanitizedText.length <= 100) {
                                              setFieldValue('payment_address', sanitizedText, true);
                                            }
                                          }}
                                          onBlur={handleBlur}
                                          value={values.payment_address}
                                          maxLength={100}
                                          disabled={!isEdittingReceipt}
                                          id="payment_address"
                                        />
                                        <label className="label" htmlFor="payment_address">
                                          {t('street_number')} <span>*</span>
                                        </label>
                                      </span>
                                      <div className={`${touched.payment_address && errors.payment_address ? 'error' : ''}`}>
                                        {touched.payment_address && errors.payment_address ? `${errors.payment_address}` : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-12">
                                    <div className="input-item">
                                      <span className="p-float-label">
                                        <InputText
                                          name="payment_additional_address"
                                          key={isEdittingReceipt ? 'enabled' : 'disabled'}
                                          onChange={(item: any) => {
                                            const text = item.target.value;
                                            const sanitizedText = text.replace(Regex_Emoji, '');
                                            if (sanitizedText.length <= 100) {
                                              setFieldValue('payment_additional_address', sanitizedText, true);
                                            }
                                          }}
                                          onBlur={handleBlur}
                                          value={values.payment_additional_address}
                                          maxLength={100}
                                          disabled={!isEdittingReceipt}
                                          id="payment_additional_address"
                                        />
                                        <label className="label" htmlFor="payment_additional_address">
                                          {t('additional_address')}
                                        </label>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-4">
                                    <div className="input-item">
                                      <span className="p-float-label">
                                        <InputText
                                          className={`${touched.payment_post_code && errors.payment_post_code ? 'has-error' : ''}`}
                                          name="payment_post_code"
                                          key={isEdittingReceipt ? 'enabled' : 'disabled'}
                                          onChange={(item: any) => {
                                            const text = item.target.value;
                                            const sanitizedText = text.replace(Regex_Emoji, '');
                                            if (sanitizedText.length <= 10) {
                                              setFieldValue('payment_post_code', sanitizedText, true);
                                            }
                                          }}
                                          onBlur={handleBlur}
                                          value={values.payment_post_code}
                                          maxLength={10}
                                          disabled={!isEdittingReceipt}
                                          id="payment_post_code"
                                        />
                                        <label className="label" htmlFor="payment_post_code">
                                          {t('postal_code')} <span>*</span>
                                        </label>
                                      </span>
                                      <div className={`${touched.payment_post_code && errors.payment_post_code ? 'error' : ''}`}>
                                        {touched.payment_post_code && errors.payment_post_code ? `${errors.payment_post_code}` : null}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-8">
                                    <div className="input-item">
                                      <span className="p-float-label">
                                        <InputText
                                          className={`${touched.payment_city && errors.payment_city ? 'has-error' : ''}`}
                                          name="payment_city"
                                          key={isEdittingReceipt ? 'enabled' : 'disabled'}
                                          onChange={(item: any) => {
                                            const text = item.target.value;
                                            const sanitizedText = text.replace(Regex_Emoji, '');
                                            if (sanitizedText.length <= 85) {
                                              setFieldValue('payment_city', sanitizedText, true);
                                            }
                                          }}
                                          onBlur={handleBlur}
                                          value={values.payment_city}
                                          maxLength={85}
                                          disabled={!isEdittingReceipt}
                                          id="payment_city"
                                        />
                                        <label className="label" htmlFor="payment_city">
                                          {t('town')} <span>*</span>
                                        </label>
                                      </span>
                                      <div className={`${touched.payment_city && errors.payment_city ? 'error' : ''}`}>
                                        {touched.payment_city && errors.payment_city ? `${errors.payment_city}` : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-12">
                                    <div className="input-item">
                                      <Dropdown
                                        className={'donation-land-dropdown'}
                                        panelClassName="donation-land-dropdown-panel"
                                        valueTemplate={selectedTemplate}
                                        name="payment_country"
                                        value={values.payment_country}
                                        disabled={!isEdittingReceipt}
                                        options={countries || []}
                                        style={{ height: 50 }}
                                        onChange={handleChange}
                                        optionLabel="name"
                                        optionValue="ISO2"
                                        filter
                                        filterBy="name"
                                        placeholder={t('select_country')}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </form>
                          );
                        }}
                      </Formik>
                      <div className="donation-certificate-field donation-certificate-btn">
                        <div className="btn-wrapper">
                          <Button
                            className="p-button-rounded wi-main-button wi-icon-edit"
                            icon="fa-solid fa-pencil"
                            iconPos="right"
                            hidden={isEdittingReceipt}
                            onClick={() => setIsEdittingReceipt(true)}
                            style={{ marginRight: 0 }}
                          />
                          <Button
                            className="p-button-rounded wi-main-button wi-icon-edit wi-icon-cancel"
                            icon="fa-solid fa-chevron-left"
                            iconPos="right"
                            hidden={!isEdittingReceipt}
                            style={{ marginRight: 10 }}
                            onClick={() => onResetForm()}
                          />
                          <Button
                            label={t('save')}
                            className="p-button-rounded wi-main-button donation-receipt-download-button wi-btn-save"
                            icon="fa-solid fa-chevron-right"
                            iconPos="right"
                            hidden={!isEdittingReceipt}
                            disabled={!(formState.isValid && formState.dirty)}
                            onClick={() => onHandleSaveReceipt()}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="donation-certificate-section col-md-6 col-12 donation-certificate-section-white-mobile">
                    <div className="table-detail-container">
                      <div className="donation-certificate-content-title">{t('donation_receipts')}</div>
                      {donation?.disabled_receipt ? (
                        <div className="donation-certificate-content-title-note">{t('donation_receipts_not_eligible')}</div>
                      ) : (
                        <>
                          <div className="donation-certificate-content-title-note" style={{ fontSize: '15px' }}>
                            {donation?.match_funding_value
                              ? t(`match_funding_label`, {
                                match_funding_value: donation?.match_funding_value?.toLocaleString(t('language')) || 0,
                                currency: donation?.currency_unit,
                              })
                              : +(donation.coupon_value || 0) > 0
                                ? t(`donation_receipts_use_coupon${+donation.coupon_value === +donation.donation_money ? '_no_payment' : ''}`, {
                                  coupon_value: donation?.coupon_value?.toLocaleString(t('language')),
                                  currency_unit: donation?.currency_unit,
                                })
                                : ''}
                          </div>
                          <div className="table-children">
                            <DataTable
                              loading={isLoading}
                              value={donationReceipts?.filter((f: any) => +f.total_amount - +(f.coupon_value || 0) > 0)}
                              scrollable
                              scrollHeight="calc(100vh - 166px)"
                              scrollDirection="both"
                              responsiveLayout="scroll"
                            >
                              {dynamicColumnsReceipt}
                            </DataTable>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation('language')(memo(ReceiptDonation));
